import { Wallet } from "lucide-react";

import type { Budget } from "@/lib/meta";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";

/**
 * Picks the budget being worked on. Always shown, even with a single budget:
 * lines and cells belong to one budget, and hiding the control made the
 * Overview and the Lines card look like they were editing something global.
 */
export function BudgetSwitcher({
  budgets,
  current,
  onSelect,
  className,
}: {
  budgets: Budget[];
  current: string | null;
  onSelect: (id: string) => void;
  className?: string;
}) {
  if (budgets.length === 0) return null;

  if (budgets.length === 1) {
    return (
      <span
        className={cn(
          "inline-flex items-center gap-1.5 rounded-md border bg-muted/40 px-2.5 py-1 text-sm font-medium",
          className,
        )}
      >
        <Wallet className="size-3.5 text-muted-foreground" />
        {budgets[0]!.name}
      </span>
    );
  }

  return (
    <div className={cn("flex flex-wrap items-center gap-1", className)} role="group" aria-label="Budget">
      <Wallet className="size-3.5 text-muted-foreground" />
      {budgets.map((b) => (
        <Button
          key={b.id}
          size="sm"
          variant={b.id === current ? "default" : "outline"}
          aria-pressed={b.id === current}
          onClick={() => onSelect(b.id)}
        >
          {b.name}
        </Button>
      ))}
    </div>
  );
}
